import Cookies from "js-cookie";
import { appStorage } from "../utils/appStorage";

const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";
const SCRIPT_ID = "razorpay-checkout-js";

const getApiBase = () => process.env.REACT_APP_API_BASE_URL || "";

export const getAuthToken = () => Cookies.get("auth_token");

const isSuccess = (json) =>
  json?.status === 1 || json?.status === "1" || json?.Status === 1 || json?.Status === "1";

const authHeaders = (token, contentType = "application/x-www-form-urlencoded") => ({
  Authorization: `Bearer ${token}`,
  ...(contentType ? { "Content-Type": contentType } : {}),
});

let scriptLoadPromise = null;

export const loadRazorpayScript = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Razorpay is only available in the browser"));
  }
  if (window.Razorpay) return Promise.resolve(window.Razorpay);
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Razorpay));
      existing.addEventListener("error", () => reject(new Error("Failed to load Razorpay")));
      if (window.Razorpay) resolve(window.Razorpay);
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error("Failed to load Razorpay checkout"));
    document.body.appendChild(script);
  });

  return scriptLoadPromise;
};

const unwrapData = (json) => {
  const raw = json?.data;
  if (Array.isArray(raw)) return raw[0] ?? raw;
  return raw ?? json;
};

/** Static plans until GET /payment/plans is available on the backend. */
export const getStaticPaymentPlans = () => FALLBACK_PLANS;

export const fetchPaymentPlans = async () => getStaticPaymentPlans();

export const fetchPaymentStatusRazorpay = async (token = getAuthToken()) => {
  if (!token) throw new Error("Please sign in to continue");

  const res = await fetch(`${getApiBase()}/payment_status_razorpay`, {
    method: "GET",
    headers: authHeaders(token, null),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok || !isSuccess(json)) {
    throw new Error(json?.message || "Failed to fetch payment status");
  }

  return unwrapData(json);
};

export const createRazorpayOrder = async (
  { planName, billingCycle },
  token = getAuthToken()
) => {
  if (!token) throw new Error("Please sign in to continue");
  if (!planName) throw new Error("Plan is required");
  if (!billingCycle) throw new Error("Billing cycle is required");

  const body = new URLSearchParams();
  body.append("plan_name", planName);
  body.append("billing_cycle", billingCycle);

  const res = await fetch(`${getApiBase()}/payment/razorpay/create-order`, {
    method: "POST",
    headers: authHeaders(token),
    body: body.toString(),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok || !isSuccess(json)) {
    throw new Error(json?.message || "Failed to create payment order");
  }

  return unwrapData(json);
};

export const verifyRazorpayPayment = async (
  { razorpay_order_id, razorpay_payment_id, razorpay_signature },
  token = getAuthToken()
) => {
  if (!token) throw new Error("Please sign in to verify payment");

  const body = new URLSearchParams();
  body.append("razorpay_order_id", razorpay_order_id);
  body.append("razorpay_payment_id", razorpay_payment_id);
  body.append("razorpay_signature", razorpay_signature);

  const res = await fetch(`${getApiBase()}/payment/razorpay/verify`, {
    method: "POST",
    headers: authHeaders(token),
    body: body.toString(),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok || !isSuccess(json)) {
    throw new Error(json?.message || "Payment verification failed");
  }

  return unwrapData(json);
};

const pickOrderField = (order, keys) => {
  for (const key of keys) {
    if (order?.[key] != null && order[key] !== "") return order[key];
  }
  return null;
};

export const persistPaymentSession = (verifyPayload = {}) => {
  try {
    appStorage.setItem("payment_status", "1");
    const planStatus =
      verifyPayload?.plan_status ??
      verifyPayload?.planStatus ??
      verifyPayload?.payment_plan_status;
    if (planStatus != null) {
      appStorage.setItem("plan_status", String(planStatus));
    }
  } catch {
    // ignore storage errors
  }
};

export const startRazorpayCheckout = async ({
  planName,
  billingCycle,
  user = {},
  onVerified,
}) => {
  const token = getAuthToken();
  const Razorpay = await loadRazorpayScript();
  const orderPayload = await createRazorpayOrder({ planName, billingCycle }, token);

  const orderId = pickOrderField(orderPayload, [
    "razorpay_order_id",
    "order_id",
    "id",
  ]);
  const key = pickOrderField(orderPayload, [
    "razorpay_key",
    "key_id",
    "razorpay_key_id",
  ]) || process.env.REACT_APP_RAZORPAY_KEY_ID;
  const amount = pickOrderField(orderPayload, ["amount", "order_amount"]);
  const currency = pickOrderField(orderPayload, ["currency"]) || "INR";

  if (!orderId) throw new Error("Invalid order response from server");
  if (!key) throw new Error("Razorpay key is missing. Contact support.");

  return new Promise((resolve, reject) => {
    const options = {
      key,
      amount,
      currency,
      name: " PhoneBookPlus",
      description: `${planName} (${billingCycle})`,
      order_id: orderId,
      prefill: {
        name: user.name || "",
        email: user.email || "",
        contact: user.phone || "",
      },
      theme: { color: "#7C4DFF" },
      handler: async (response) => {
        try {
          const verified = await verifyRazorpayPayment(
            {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            },
            token
          );
          persistPaymentSession(verified);
          if (typeof onVerified === "function") onVerified(verified);
          resolve({ verified, razorpay: response });
        } catch (err) {
          reject(err);
        }
      },
      modal: {
        ondismiss: () => reject(new Error("Payment cancelled")),
      },
    };

    const instance = new Razorpay(options);
    instance.on("payment.failed", (event) => {
      const description =
        event?.error?.description || event?.error?.reason || "Payment failed";
      reject(new Error(description));
    });
    instance.open();
  });
};

export const PAYABLE_PLAN_NAMES = ["Basic Plus", "Basic Pro"];

export const BILLING_CYCLES = [
  { id: "monthly", label: "Monthly" },
  { id: "annual", label: "Annual" },
];

export const FALLBACK_PLANS = [
  {
    plan_name: "Basic",
    name: "Basic",
    new_name: "Essential",
    priceLabel: "Free",
    premium: false,
    description: "Your core toolkit for smart contact management and clutter-free communication.",
    features: ["Unified Contact Card (Verified email & phone)", "Custom Network Visibility (Full privacy control)", "Profile-Based Chat (Number-agnostic messaging)", "Digital Contact Card (Quick sharing via QR code)","Separated Chats (Move & pin up to 3 groups to the personal chat section)","Smart Organization (Custom tags & contact notes)","Storage & Limits (30 days cloud storage | Up to 1,000 contacts)"],
  },
  {
    plan_name: "Basic Plus",
    name: "Basic Plus",
    new_name: "Advanced",
    priceLabel: null,
    monthly_price: 299,
    annual_price: 2999,
    premium: true,
    description: "Enhanced networking power for professionals managing a growing ecosystem.",
    features: ["All Essential Features Included", "Dual Identity (2nd profile picture)", "Expanded Messaging (Move & pin up to 5 groups to the personal chat section)","Expanded Capacity (60 days cloud storage | Up to 3,000 contacts)","Work Experience Section (Professional details)",],
  },
  {
    plan_name: "Basic Pro",
    name: "Basic Pro",
    new_name: "Professional",
    priceLabel: null,
    monthly_price: 499,
    annual_price: 4999,
    premium: true,
    description: "The ultimate platform for power networkers and community leaders.",
    features: ["All Advanced Features Included", "Maximum Presence (3rd profile picture)", "Max Messaging Power (Move & pin up to 10 groups to the personal chat section)", "Massive Scale (90 days cloud storage | Up to 7,500 contacts)", "Future-Proof (Priority access to all upcoming features)"],
  },
];

export const mergePlansWithFallback = (apiPlans = []) => {
  if (!apiPlans.length) return FALLBACK_PLANS;
  return getStaticPaymentPlans();
};

/** Backend plan identifiers → user-facing labels (API payloads stay unchanged). */
export const PLAN_DISPLAY_NAMES = {
  Basic: "Essential",
  "Basic Plus": "Advanced",
  "Basic Pro": "Professional",
};

export const getPlanDisplayName = (planOrName) => {
  if (planOrName == null || planOrName === "") return "";
  if (typeof planOrName === "string") {
    return PLAN_DISPLAY_NAMES[planOrName] || planOrName;
  }
  const backendName =
    planOrName.plan_name || planOrName.name || planOrName.plan_type || "";
  return planOrName.new_name || PLAN_DISPLAY_NAMES[backendName] || backendName;
};

export const formatPlanPrice = (plan, billingCycle) => {
  if (!plan?.premium) return "Free";
  const monthly = plan.monthly_price ?? plan.monthly_amount;
  const annual = plan.annual_price ?? plan.annual_amount;
  const amount = billingCycle === "annual" ? annual : monthly;
  if (amount == null) return "—";
  const currency = plan.currency || "INR";
  if (currency === "INR") return `₹${Number(amount).toLocaleString("en-IN")}`;
  return `$${Number(amount).toFixed(2)}`;
};
